import { type FC, type ReactNode, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import getBuyerSupportLink from 'dibs-support-link/exports/getBuyerSupportLink.ts';
import BuyerIncentiveCopy from 'dibs-buyer-incentives/exports/BuyerIncentiveCopy.ts';
import { getBuyerProtectionDuration } from 'dibs-buyer-incentives/exports/getBuyerProtectionDuration.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

import CheckShield from 'dibs-icons/exports/CheckShield.ts';
import MoneyBackGuarantee from 'dibs-icons/exports/MoneyBackGuarantee.ts';
import Cancellation24Hours from 'dibs-icons/exports/Cancellation24Hours.ts';
import Storefront from 'dibs-icons/exports/Storefront.ts';
import PriceParity from 'dibs-icons/exports/PriceParity.ts';
import Delivery from 'dibs-icons/exports/Delivery.ts';
import Returns from 'dibs-icons/exports/Returns.ts';

import { PdpSharedBuyerIncentivesLink } from '../PdpSharedBuyerIncentivesLink/PdpSharedBuyerIncentivesLink.tsx';

import { type PdpSharedBuyerIncentives_user$key } from './__generated__/PdpSharedBuyerIncentives_user.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './PdpSharedBuyerIncentives.scss';

const Incentive: FC<{
    title: ReactNode;
    description: ReactNode;
    icon?: ReactNode;
    plainIcon?: boolean;
}> = ({ title, description, icon, plainIcon = false }) => {
    return (
        <div className={dibsCss.sassyFontBodyMedium}>
            {icon &&
                (plainIcon ? (
                    <span
                        className={classnames(
                            dibsCss.inlineFlex,
                            dibsCss.itemsCenter,
                            dibsCss.justifyStart,
                            dibsCss.mbXsmall,
                            dibsCss.alignTop,
                            dibsCss.fillBlue600
                        )}
                    >
                        {icon}
                    </span>
                ) : (
                    <span
                        className={classnames(
                            dibsCss.inlineFlex,
                            dibsCss.flexShrink0,
                            dibsCss.itemsCenter,
                            dibsCss.justifyCenter,
                            dibsCss.roundedFull,
                            dibsCss.bgCyan100,
                            dibsCss.fillBlue600,
                            dibsCss.mbSmaller,
                            dibsCss.alignTop,
                            styles.circle
                        )}
                    >
                        {icon}
                    </span>
                ))}

            <div className={classnames(dibsCss.mbXxsmall, dibsCss.sassyFontBodyMediumHeavy)}>
                {title}
            </div>
            {description}
        </div>
    );
};

const getBuyerProtectionTier = (code?: string | null): ReactNode => {
    const tradeMemberCopy = (
        <FormattedMessage id="PdpSharedBuyerIncentives.member" defaultMessage="rewards member" />
    );

    switch (code) {
        case 'MEMBER':
            return tradeMemberCopy;
        case 'BRONZE':
            return (
                <FormattedMessage
                    id="PdpSharedBuyerIncentives.bronze"
                    defaultMessage="Bronze rewards member"
                />
            );
        case 'SILVER':
            return (
                <FormattedMessage
                    id="PdpSharedBuyerIncentives.silver"
                    defaultMessage="Silver rewards member"
                />
            );
        case 'GOLD':
            return (
                <FormattedMessage
                    id="PdpSharedBuyerIncentives.gold"
                    defaultMessage="Gold rewards member"
                />
            );
        case 'PLATINUM':
            return (
                <FormattedMessage
                    id="PdpSharedBuyerIncentives.platinum"
                    defaultMessage="Platinum rewards member"
                />
            );
        default:
            return tradeMemberCopy;
    }
};

const userFragment = graphql`
    fragment PdpSharedBuyerIncentives_user on User {
        isVerifiedTrade
        tradeFirm @include(if: $isTrade) {
            rewards {
                currentProgram {
                    programInfo {
                        code
                    }
                    childPrograms(filter: "ENHANCED_BUYER_PROTECTION") {
                        code
                    }
                }
            }
        }
    }
`;

export const PdpSharedBuyerIncentives: FC<{
    user: PdpSharedBuyerIncentives_user$key | null | undefined;
    showLearnMoreLink?: boolean;
    columnLayoutOverride?: boolean;
}> = ({ user: userRef, showLearnMoreLink = false, columnLayoutOverride = false }) => {
    const intl = useIntl();
    const user = useFragment(userFragment, userRef);
    const { isMobile } = useServerVarsContext();
    const isTradeUser = !!user?.isVerifiedTrade;

    const tradeUserRewardsProgram = user?.tradeFirm?.rewards?.currentProgram;
    const tradeProtectionTierCode = tradeUserRewardsProgram?.programInfo?.code;
    const tradeProtectionDurationCode = tradeUserRewardsProgram?.childPrograms?.[0]?.code;
    const showTradeRewardsCopy = !!tradeProtectionDurationCode;

    const authenticityArticleUrl = getBuyerSupportLink('/en-us/articles/20416652048027');
    const moneyBackArticleUrl = getBuyerSupportLink('/en-us/articles/14552578793371');
    const priceMatchArticleUrl = getBuyerSupportLink('/en-us/articles/15203720988699');
    const cancellationArticleUrl = getBuyerSupportLink('/en-us/articles/14544967384731');
    const shippingArticleUrl = getBuyerSupportLink('/en-us/articles/14516879314587');
    const returnPolicyArticleUrl = getBuyerSupportLink('/en-us/articles/33803789395867');
    const vettedSellersUrl = handleLocaleUrl('/about/vetted-sellers/', intl.locale);

    const renderInlineLearnMore = useCallback(
        (trigger: string, url: string) => (
            <Link
                href={url}
                onClick={event =>
                    trackEvent(
                        {
                            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                            interaction_type:
                                interactionTypeConstants.INTERACTION_TYPE_PURCHASE_PROTECTION,
                            step_interaction_name:
                                stepInteractionConstants.STEP_INTERACTION_LEARN_MORE_CLICK,
                            trigger,
                        },
                        event
                    )
                }
                target="_blank"
                underline="underline"
            >
                {intl.formatMessage({
                    id: 'PdpSharedBuyerIncentives.learnMore',
                    defaultMessage: 'Learn More',
                })}
            </Link>
        ),
        [intl]
    );

    return (
        <>
            <div
                className={classnames(
                    styles.wrapper,
                    styles.isDibsPromiseVariant,
                    dibsCss.grid,
                    dibsCss.gapYMedium,
                    {
                        [styles.columnOverride]: columnLayoutOverride || !!isMobile,
                    },
                    isMobile ? dibsCss.textLeft : null,
                    isMobile ? dibsCss.gapXNone : dibsCss.gapXLarge
                )}
            >
                {isTradeUser ? (
                    <>
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<CheckShield variation="24" />}
                            title={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.trade.authenticityTitle"
                                    defaultMessage="Authenticity Guaranteed"
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.trade.authenticityBody"
                                    defaultMessage="Your reputation is built on quality, and so is ours. If any item is found to be non-authentic within one year, we provide a full refund. {learnMore}"
                                    values={{
                                        learnMore: renderInlineLearnMore(
                                            'authenticity guaranteed',
                                            authenticityArticleUrl
                                        ),
                                    }}
                                />
                            }
                        />
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<Returns variation="24" />}
                            title={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.trade.returnPolicyTitle"
                                    defaultMessage="Industry Leading Return Policy"
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.trade.returnPolicyBody"
                                    defaultMessage="Plans change. We offer a guaranteed return window specifically for our Trade members, giving you the time you need to ensure a piece is right for the space. Plus, we cover any restocking fees for you. {learnMore}"
                                    values={{
                                        learnMore: renderInlineLearnMore(
                                            'industry leading return policy',
                                            returnPolicyArticleUrl
                                        ),
                                    }}
                                />
                            }
                        />
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<MoneyBackGuarantee variation="24" />}
                            title={<BuyerIncentiveCopy name="moneyBackGuarantee" />}
                            description={
                                showTradeRewardsCopy ? (
                                    <FormattedMessage
                                        id="PdpSharedBuyerIncentives.buyerProtectionTradeBody"
                                        defaultMessage="If your item is not as described, is damaged in transit, or does not arrive, contact us for a full refund. As a {tier}, you get {days} days to report any issues you have with the order. {learnMore}"
                                        values={{
                                            learnMore: renderInlineLearnMore(
                                                'money back guarantee',
                                                moneyBackArticleUrl
                                            ),
                                            tier: getBuyerProtectionTier(tradeProtectionTierCode),
                                            days: getBuyerProtectionDuration(
                                                tradeProtectionDurationCode
                                            ),
                                        }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="PdpSharedBuyerIncentives.buyerProtectionBody"
                                        defaultMessage="If your item is not as described, is damaged in transit, or does not arrive, contact us within 7 days for a full refund. {learnMore}"
                                        values={{
                                            learnMore: renderInlineLearnMore(
                                                'money back guarantee',
                                                moneyBackArticleUrl
                                            ),
                                        }}
                                    />
                                )
                            }
                        />
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<PriceParity variation="24" />}
                            title={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.trade.priceMatchTitle"
                                    defaultMessage="Trade Price Match"
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.trade.priceMatchBody"
                                    defaultMessage="We ensure you always get the best value for your client. If the same seller has the item listed lower elsewhere, we’ll match it on the spot. {learnMore}"
                                    values={{
                                        learnMore: renderInlineLearnMore(
                                            'trade price match',
                                            priceMatchArticleUrl
                                        ),
                                    }}
                                />
                            }
                        />
                    </>
                ) : (
                    <>
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<CheckShield variation="24" />}
                            title={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.authenticityTitle"
                                    defaultMessage="Authenticity Guaranteed"
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.authenticityBody"
                                    defaultMessage="If it isn’t genuine, you get your money back. We give you a full year to flag any issues with an item's authenticity for a 100% refund. {learnMore}"
                                    values={{
                                        learnMore: renderInlineLearnMore(
                                            'authenticity guaranteed',
                                            authenticityArticleUrl
                                        ),
                                    }}
                                />
                            }
                        />
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<Storefront variation="24" />}
                            title={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.sellersTitle"
                                    defaultMessage="Sellers We Know and Trust"
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.sellersBody"
                                    defaultMessage="We don’t let just anyone sell here. Every seller on our platform is a pro who has passed our strict quality and service standards before they can list a single item. {learnMore}"
                                    values={{
                                        learnMore: renderInlineLearnMore(
                                            'sellers we know and trust',
                                            vettedSellersUrl
                                        ),
                                    }}
                                />
                            }
                        />
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<MoneyBackGuarantee variation="24" />}
                            title={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.moneyBackTitle"
                                    defaultMessage="Money Back Guarantee"
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.moneyBackBody"
                                    defaultMessage="If your item is damaged, never arrives, or doesn’t look as expected, tell us within 7 days. We’ll step in and get your money back. {learnMore}"
                                    values={{
                                        learnMore: renderInlineLearnMore(
                                            'money back guarantee',
                                            moneyBackArticleUrl
                                        ),
                                    }}
                                />
                            }
                        />
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<PriceParity variation="24" />}
                            title={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.priceMatchTitle"
                                    defaultMessage="Price Matching"
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.priceMatchBody"
                                    defaultMessage="You shouldn't have to shop around. If the same seller has this item listed for less anywhere else, we’ll match that price. {learnMore}"
                                    values={{
                                        learnMore: renderInlineLearnMore(
                                            'price matching',
                                            priceMatchArticleUrl
                                        ),
                                    }}
                                />
                            }
                        />
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<Cancellation24Hours variation="24" />}
                            title={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.secondThoughtsTitle"
                                    defaultMessage='24-Hour "Second Thoughts"'
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.secondThoughtsBody"
                                    defaultMessage="Buyer’s remorse happens. You have a full 24 hours to cancel your order for any reason — no questions asked and no fees. {learnMore}"
                                    values={{
                                        learnMore: renderInlineLearnMore(
                                            '24-hour second thoughts',
                                            cancellationArticleUrl
                                        ),
                                    }}
                                />
                            }
                        />
                        <Incentive
                            plainIcon={!!isMobile}
                            icon={<Delivery variation="24" />}
                            title={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.deliveryTitle"
                                    defaultMessage="Protected Global Delivery"
                                />
                            }
                            description={
                                <FormattedMessage
                                    id="PdpSharedBuyerIncentives.consumer.deliveryBody"
                                    defaultMessage="We use a specialized carrier network that knows how to handle luxury goods. Your piece is tracked, protected and delivered exactly where you want it. {learnMore}"
                                    values={{
                                        learnMore: renderInlineLearnMore(
                                            'protected global delivery',
                                            shippingArticleUrl
                                        ),
                                    }}
                                />
                            }
                        />
                    </>
                )}
            </div>
            {showLearnMoreLink && <PdpSharedBuyerIncentivesLink />}
        </>
    );
};
